import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { X } from "lucide-react";
import UpdateSlide from "./UpdateSlide";
import { preloadStoryItems } from "./preloadStories";
import { resolveMediaBlocks } from "./textLayout";

export default function SystemUpdateViewer({ items, startIndex = 0, onClose, onViewed }) {
  const [slides] = useState(() => (Array.isArray(items) ? items.slice() : []));
  const [index, setIndex] = useState(() => {
    const max = Math.max(0, (items?.length || 1) - 1);
    return Math.min(Math.max(0, startIndex), max);
  });
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches
  );
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1200,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));
  const [mediaSize, setMediaSize] = useState(null);
  const videoRef = useRef(null);
  const viewedRef = useRef(new Set());
  const holdTimerRef = useRef(null);
  const holdStartRef = useRef(0);
  const draggedRef = useRef(false);
  const closingRef = useRef(false);
  const ignoreUntilRef = useRef(performance.now() + 400);
  const goNextRef = useRef(() => {});
  const elapsedRef = useRef(0);
  const y = useMotionValue(0);
  const backdropOpacity = useTransform(y, [0, 260], [0.8, 0.15]);
  const cardScale = useTransform(y, [0, 260], [1, 0.92]);

  const current = slides[index];
  const isVideo = resolveMediaBlocks(current).some((block) => block.type === "video" && block.url);

  const goNext = useCallback(() => {
    if (index >= slides.length - 1) {
      onClose?.();
      return;
    }
    setIndex((i) => i + 1);
    setProgress(0);
  }, [index, slides.length, onClose]);

  const goPrev = useCallback(() => {
    if (index <= 0) {
      setProgress(0);
      if (videoRef.current) videoRef.current.currentTime = 0;
      return;
    }
    setIndex((i) => i - 1);
    setProgress(0);
  }, [index]);

  goNextRef.current = goNext;

  const onMediaSize = useCallback((size) => {
    if (!size?.width || !size?.height) return;
    setMediaSize((prev) => {
      if (prev && prev.width === size.width && prev.height === size.height) return prev;
      return { width: size.width, height: size.height };
    });
  }, []);

  useEffect(() => {
    preloadStoryItems(slides.slice(index, index + 3));
  }, [index, slides]);

  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
    setMediaSize(null);
  }, [index, current?.id]);

  useEffect(() => {
    if (!isVideo) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const report = () => {
      if (el.videoWidth && el.videoHeight) {
        onMediaSize({ width: el.videoWidth, height: el.videoHeight });
      }
    };
    report();
    el.addEventListener("loadedmetadata", report);
    el.addEventListener("loadeddata", report);
    return () => {
      el.removeEventListener("loadedmetadata", report);
      el.removeEventListener("loadeddata", report);
    };
  }, [isVideo, index, current?.id, onMediaSize]);

  useEffect(() => {
    if (!current?.id || viewedRef.current.has(current.id)) return;
    viewedRef.current.add(current.id);
    onViewed?.(current.id);
  }, [current?.id, onViewed]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const syncDesktop = () => setIsDesktop(mq.matches);
    const syncViewport = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    syncDesktop();
    syncViewport();
    mq.addEventListener("change", syncDesktop);
    window.addEventListener("resize", syncViewport);
    return () => {
      mq.removeEventListener("change", syncDesktop);
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  const desktopBox = useMemo(() => {
    const maxW = Math.min(480, viewport.w - 32);
    const maxH = viewport.h - 32;
    const mw = mediaSize?.width > 0 ? mediaSize.width : 9;
    const mh = mediaSize?.height > 0 ? mediaSize.height : 16;
    const scale = Math.min(maxW / mw, maxH / mh);
    return {
      width: Math.max(200, Math.round(mw * scale)),
      height: Math.max(280, Math.round(mh * scale)),
    };
  }, [mediaSize, viewport.h, viewport.w]);

  useEffect(() => {
    if (paused) {
      videoRef.current?.pause?.();
      return undefined;
    }

    const durationMs = Math.max(2000, (current?.duration_sec || 7) * 1000);
    const started = performance.now() - elapsedRef.current;
    let raf;

    const tick = (now) => {
      elapsedRef.current = now - started;
      let p = elapsedRef.current / durationMs;
      if (isVideo && videoRef.current && videoRef.current.duration > 0) {
        p = videoRef.current.currentTime / videoRef.current.duration;
      }
      p = Math.min(1, p);
      setProgress(p);
      if (p >= 1) {
        goNextRef.current();
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    if (isVideo) videoRef.current?.play?.().catch(() => {});

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [index, paused, current?.id, current?.duration_sec, isVideo]);

  const closeNicely = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    animate(y, 720, {
      duration: 0.26,
      ease: [0.32, 0, 0.67, 0],
      onComplete: () => onClose?.(),
    });
    window.setTimeout(() => onClose?.(), 280);
  }, [onClose, y]);

  const clearHold = () => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const onTapPointerDown = () => {
    holdStartRef.current = performance.now();
    clearHold();
    holdTimerRef.current = window.setTimeout(() => setPaused(true), 180);
  };

  const onTapPointerUp = () => {
    clearHold();
    setPaused(false);
  };

  const canTap = () => {
    if (draggedRef.current) return false;
    if (performance.now() < ignoreUntilRef.current) return false;
    if (performance.now() - holdStartRef.current > 280) return false;
    return true;
  };

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[120]" dir="rtl">
      <motion.div className="absolute inset-0 bg-black" style={{ opacity: backdropOpacity }} />
      <div className="relative flex h-full w-full items-center justify-center p-0 sm:p-4">
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0.04, bottom: 0.72 }}
          dragMomentum={false}
          style={isDesktop ? { y, scale: cardScale, width: desktopBox.width, height: desktopBox.height } : { y, scale: cardScale }}
          onDrag={(_, info) => {
            if (info.offset.y > 12) {
              draggedRef.current = true;
              setPaused(true);
            }
          }}
          onDragEnd={(_, info) => {
            const shouldClose = info.offset.y > 110 || info.velocity.y > 700;
            if (shouldClose) {
              closeNicely();
              return;
            }
            animate(y, 0, { type: "spring", stiffness: 460, damping: 36 });
            setPaused(false);
            window.setTimeout(() => {
              draggedRef.current = false;
            }, 80);
          }}
          className="relative h-full w-full touch-none overflow-hidden bg-black sm:rounded-3xl sm:shadow-2xl"
        >
          <div className="absolute inset-x-0 top-0 z-20 flex gap-1 px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pointer-events-none sm:px-4 sm:pt-4">
            {slides.map((item, i) => (
              <div key={item.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/35">
                <div
                  className="h-full rounded-full bg-white"
                  style={{
                    width: i < index ? "100%" : i === index ? `${Math.max(progress, 0) * 100}%` : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={closeNicely}
            aria-label="סגור"
            className="absolute left-3 top-7 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white"
          >
            <X className="h-5 w-5" />
          </button>

          <UpdateSlide
            key={current.id}
            update={current}
            videoRef={videoRef}
            onVideoEnd={() => goNextRef.current()}
            playing={!paused}
            objectFit={isDesktop ? "contain" : "cover"}
            onMediaSize={onMediaSize}
          />

          <div className="absolute inset-0 z-10 flex">
            <button
              type="button"
              className="h-full w-1/3"
              aria-label="הקודם"
              onClick={() => {
                if (canTap()) goPrev();
              }}
              onPointerDown={onTapPointerDown}
              onPointerUp={onTapPointerUp}
              onPointerCancel={onTapPointerUp}
            />
            <button
              type="button"
              className="h-full w-2/3"
              aria-label="הבא"
              onClick={() => {
                if (canTap()) goNext();
              }}
              onPointerDown={onTapPointerDown}
              onPointerUp={onTapPointerUp}
              onPointerCancel={onTapPointerUp}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
